import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Search, Filter, Calendar, User, MapPin } from 'lucide-react';

interface Report {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'verified' | 'valuated' | 'certified';
  submittedBy: string;
  submittedAt: string;
  location: string;
  category: string;
}

// Mock data - replace with API call
const mockReports: Report[] = [
  {
    id: '1',
    title: 'Organic Farm Certification Request',
    description: 'Request for organic certification for 50 hectares of farmland',
    status: 'pending',
    submittedBy: 'João Silva',
    submittedAt: '2024-01-15T10:30:00Z',
    location: 'São Paulo, Brazil',
    category: 'Organic Farming',
  },
  {
    id: '2',
    title: 'Carbon Credit Verification',
    description: 'Verification of carbon credits for reforestation project',
    status: 'verified',
    submittedBy: 'Maria Santos',
    submittedAt: '2024-01-14T14:20:00Z',
    location: 'Amazonas, Brazil',
    category: 'Carbon Credits',
  },
  {
    id: '3',
    title: 'Sustainable Agriculture Report',
    description: 'Annual sustainability report for agricultural cooperative',
    status: 'valuated',
    submittedBy: 'Pedro Costa',
    submittedAt: '2024-01-13T09:15:00Z',
    location: 'Minas Gerais, Brazil',
    category: 'Sustainability',
  },
  {
    id: '4',
    title: 'Water Conservation Project',
    description: 'Report on water conservation practices implementation',
    status: 'certified',
    submittedBy: 'Ana Oliveira',
    submittedAt: '2024-01-12T16:45:00Z',
    location: 'Rio de Janeiro, Brazil',
    category: 'Water Management',
  },
];

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  verified: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  valuated: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  certified: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
};

const Reports = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const filteredReports = mockReports.filter(report => {
    const matchesSearch =
      report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.submittedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.category.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || report.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleReportClick = (reportId: string) => {
    navigate(`/reports/${reportId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Reports</h1>
          <p className="text-muted-foreground mt-2">
            Review and process submitted reports through the verification workflow
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="flex gap-2 items-center">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="valuated">Valuated</option>
                <option value="certified">Certified</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { status: 'pending', label: 'Pending Review', count: mockReports.filter(r => r.status === 'pending').length },
          { status: 'verified', label: 'Verified', count: mockReports.filter(r => r.status === 'verified').length },
          { status: 'valuated', label: 'Valuated', count: mockReports.filter(r => r.status === 'valuated').length },
          { status: 'certified', label: 'Certified', count: mockReports.filter(r => r.status === 'certified').length },
        ].map(({ status, label, count }) => (
          <Card
            key={status}
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setFilterStatus(status)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold mt-1">{count}</p>
                </div>
                <Badge className={statusColors[status as keyof typeof statusColors]} variant="secondary">
                  {status}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredReports.map((report) => (
          <Card
            key={report.id}
            className="hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => handleReportClick(report.id)}
          >
            <CardHeader>
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg truncate">{report.title}</CardTitle>
                  <CardDescription className="mt-2 line-clamp-2">
                    {report.description}
                  </CardDescription>
                </div>
                <Badge className={statusColors[report.status]} variant="secondary">
                  {report.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>{report.submittedBy}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(report.submittedAt)}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>{report.location}</span>
                </div>
                <div className="pt-2">
                  <Badge variant="outline">{report.category}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredReports.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm || filterStatus !== 'all'
                ? 'No reports match your filters'
                : 'No reports yet'
              }
            </h3>
            <p className="text-muted-foreground text-center">
              {searchTerm || filterStatus !== 'all'
                ? 'Try adjusting your search criteria or filters'
                : 'Reports will appear here once they are submitted'
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reports;
